import { AgentAPIRequest, AgentAPIResponse } from './AgentAPIGateway';

export interface SegmentCriteria {
  id: string;
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'in' | 'not_in' | 'between';
  value: any;
  weight?: number;
  logicalOperator?: 'AND' | 'OR';
}

export interface AudienceSegment {
  id: string;
  name: string;
  description?: string;
  criteria: SegmentCriteria[];
  size: number;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  usageCount: number;
  lastUsed?: Date;
  expectedPerformance: {
    openRate: number;
    clickRate: number;
    conversionRate: number;
  };
  status: 'active' | 'inactive' | 'draft';
  aiGenerated?: boolean;
}

export interface SegmentMember {
  segmentId: string;
  leadId: string;
  addedAt: Date;
  addedBy: string;
  isActive: boolean;
}

export interface SegmentPerformance {
  segmentId: string;
  totalCampaigns: number;
  totalEmails: number;
  averageOpenRate: number;
  averageClickRate: number;
  averageConversionRate: number;
  lastCampaignDate?: Date;
  performanceTrend: 'up' | 'down' | 'stable';
  benchmarkComparison: {
    vsAverage: number; // percentage difference from platform average
    ranking: number; // ranking among all segments (1 = best)
  };
}

export interface SegmentOptimization {
  segmentId: string;
  suggestions: {
    type: 'criteria' | 'size' | 'engagement' | 'performance';
    priority: 'high' | 'medium' | 'low';
    title: string;
    description: string;
    expectedImpact: string;
    actionRequired: string;
  }[];
  sizeRecommendation: {
    current: number;
    recommended: number;
    reason: string;
  };
  criteriaRecommendations: {
    add?: SegmentCriteria[];
    modify?: { criteriaId: string; suggestion: string }[];
    remove?: string[];
  };
}

export class AgentSegmentService {
  private segments: AudienceSegment[] = [];
  private segmentMembers: SegmentMember[] = [];
  private leads: any[] = [];

  constructor() {
    this.initializeMockData();
  }

  private initializeMockData() {
    // Mock segments data
    this.segments = [
      {
        id: 'segment_1',
        name: 'High-Value Prospects',
        description: 'Hot leads with high scores and recent engagement',
        criteria: [
          {
            id: 'criteria_1',
            field: 'status',
            operator: 'equals',
            value: 'hot',
            weight: 1
          },
          {
            id: 'criteria_2',
            field: 'leadScore',
            operator: 'greater_than',
            value: 70,
            weight: 0.8,
            logicalOperator: 'AND'
          }
        ],
        size: 245,
        tags: ['high-priority', 'sales-ready'],
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-20'),
        createdBy: 'agent_1',
        usageCount: 12,
        lastUsed: new Date('2024-01-25'),
        expectedPerformance: {
          openRate: 0.35,
          clickRate: 0.08,
          conversionRate: 0.05
        },
        status: 'active'
      },
      {
        id: 'segment_2',
        name: 'Nurture Campaign Audience',
        description: 'Warm leads from social media requiring nurturing',
        criteria: [
          {
            id: 'criteria_3',
            field: 'status',
            operator: 'equals',
            value: 'warm',
            weight: 1
          },
          {
            id: 'criteria_4',
            field: 'source',
            operator: 'equals',
            value: 'Social Media',
            weight: 0.9,
            logicalOperator: 'AND'
          }
        ],
        size: 1250,
        tags: ['nurture', 'social-media'],
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-01-18'),
        createdBy: 'agent_2',
        usageCount: 8,
        lastUsed: new Date('2024-01-22'),
        expectedPerformance: {
          openRate: 0.28,
          clickRate: 0.06,
          conversionRate: 0.03
        },
        status: 'active',
        aiGenerated: true
      },
      {
        id: 'segment_3',
        name: 'Geographic - West Coast',
        description: 'All leads from California, Oregon, and Washington',
        criteria: [
          {
            id: 'criteria_5',
            field: 'location',
            operator: 'in',
            value: ['California', 'Oregon', 'Washington'],
            weight: 1
          }
        ],
        size: 890,
        tags: ['geographic', 'west-coast'],
        createdAt: new Date('2024-01-12'),
        updatedAt: new Date('2024-01-16'),
        createdBy: 'user',
        usageCount: 5,
        lastUsed: new Date('2024-01-20'),
        expectedPerformance: {
          openRate: 0.25,
          clickRate: 0.05,
          conversionRate: 0.025
        },
        status: 'active'
      }
    ];

    // Mock segment members
    this.segmentMembers = [
      { segmentId: 'segment_1', leadId: 'lead_1', addedAt: new Date('2024-01-15'), addedBy: 'agent_1', isActive: true },
      { segmentId: 'segment_1', leadId: 'lead_2', addedAt: new Date('2024-01-16'), addedBy: 'agent_1', isActive: true },
      { segmentId: 'segment_2', leadId: 'lead_3', addedAt: new Date('2024-01-10'), addedBy: 'agent_2', isActive: true },
      { segmentId: 'segment_2', leadId: 'lead_4', addedAt: new Date('2024-01-11'), addedBy: 'agent_2', isActive: true },
      { segmentId: 'segment_3', leadId: 'lead_5', addedAt: new Date('2024-01-12'), addedBy: 'user', isActive: true }
    ];

    // Mock leads data for segment calculation
    this.leads = [
      { id: 'lead_1', name: 'John Doe', email: 'john@example.com', status: 'hot', leadScore: 85, source: 'Website Form', location: 'California' },
      { id: 'lead_2', name: 'Jane Smith', email: 'jane@example.com', status: 'hot', leadScore: 92, source: 'Referral', location: 'New York' },
      { id: 'lead_3', name: 'Bob Johnson', email: 'bob@example.com', status: 'warm', leadScore: 65, source: 'Social Media', location: 'Texas' },
      { id: 'lead_4', name: 'Alice Brown', email: 'alice@example.com', status: 'warm', leadScore: 58, source: 'Social Media', location: 'Oregon' },
      { id: 'lead_5', name: 'Charlie Wilson', email: 'charlie@example.com', status: 'cold', leadScore: 35, source: 'Advertisement', location: 'Washington' }
    ];
  }

  // CRUD Operations
  async createSegment(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      // For POST requests, data is in body; for GET requests, it's in parameters
      const data = request.method === 'POST' ? request.body : request.parameters;
      const { name, description, criteria, tags, expectedPerformance } = data;

      // Validate required fields
      if (!name || !criteria || !Array.isArray(criteria) || criteria.length === 0) {
        return this.createErrorResponse({
          code: 'VALIDATION_ERROR',
          message: 'Name and criteria are required',
          details: { requiredFields: ['name', 'criteria'] }
        }, request.requestId, 0, 400);
      }

      // Validate criteria
      const validationResult = this.validateCriteria(criteria);
      if (!validationResult.isValid) {
        return this.createErrorResponse({
          code: 'INVALID_CRITERIA',
          message: 'Invalid segment criteria',
          details: { errors: validationResult.errors }
        }, request.requestId, 0, 400);
      }

      // Calculate segment size
      const size = this.calculateSegmentSize(criteria);

      const newSegment: AudienceSegment = {
        id: `segment_${Date.now()}`,
        name,
        description: description || '',
        criteria: criteria.map((c: any, index: number) => ({
          ...c,
          id: c.id || `criteria_${Date.now()}_${index}`,
          weight: c.weight || 1
        })),
        size,
        tags: tags || [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: request.agentId,
        usageCount: 0,
        expectedPerformance: expectedPerformance || {
          openRate: 0.25,
          clickRate: 0.05,
          conversionRate: 0.025
        },
        status: 'active'
      };

      this.segments.push(newSegment);

      return {
        status: 201,
        headers: {},
        data: newSegment,
        duration: 50,
        requestId: request.requestId,
        success: true,
        metadata: {
          requestId: request.requestId,
          timestamp: new Date(),
          processingTime: 50,
          rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
        }
      };
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  async getSegment(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId } = request.parameters;
      const segment = this.segments.find(s => s.id === segmentId);

      if (!segment) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      return this.createSuccessResponse(segment, request.requestId, 15);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  async updateSegment(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId, ...updates } = request.parameters;
      const segmentIndex = this.segments.findIndex(s => s.id === segmentId);

      if (segmentIndex === -1) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      // Validate criteria if provided
      if (updates.criteria) {
        const validationResult = this.validateCriteria(updates.criteria);
        if (!validationResult.isValid) {
          return this.createErrorResponse({
            code: 'INVALID_CRITERIA',
            message: 'Invalid segment criteria',
            details: { errors: validationResult.errors }
          }, request.requestId, 0, 400);
        }

        // Recalculate size if criteria changed
        updates.size = this.calculateSegmentSize(updates.criteria);
      }

      const updatedSegment = {
        ...this.segments[segmentIndex],
        ...updates,
        updatedAt: new Date()
      };

      this.segments[segmentIndex] = updatedSegment;

      return this.createSuccessResponse(updatedSegment, request.requestId, 30);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  async deleteSegment(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId } = request.parameters;
      const segmentIndex = this.segments.findIndex(s => s.id === segmentId);

      if (segmentIndex === -1) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      // Remove segment and its members
      this.segments.splice(segmentIndex, 1);
      this.segmentMembers = this.segmentMembers.filter(m => m.segmentId !== segmentId);

      return this.createSuccessResponse({ message: 'Segment deleted successfully', segmentId }, request.requestId, 25);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  async listSegments(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { 
        page = 1, 
        limit = 20, 
        status, 
        tags, 
        createdBy,
        sortBy = 'updatedAt',
        sortOrder = 'desc'
      } = request.parameters;

      let filteredSegments = [...this.segments];

      // Apply filters
      if (status) {
        filteredSegments = filteredSegments.filter(s => s.status === status);
      }

      if (tags && Array.isArray(tags)) {
        filteredSegments = filteredSegments.filter(s => 
          s.tags && tags.some(tag => s.tags!.includes(tag))
        );
      }

      if (createdBy) {
        filteredSegments = filteredSegments.filter(s => s.createdBy === createdBy);
      }

      // Apply sorting
      filteredSegments.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (sortBy) {
          case 'name':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'size':
            aValue = a.size;
            bValue = b.size;
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
            break;
          case 'updatedAt':
            aValue = new Date(a.updatedAt);
            bValue = new Date(b.updatedAt);
            break;
          case 'usageCount':
            aValue = a.usageCount;
            bValue = b.usageCount;
            break;
          default:
            aValue = new Date(a.updatedAt);
            bValue = new Date(b.updatedAt);
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      // Apply pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedSegments = filteredSegments.slice(startIndex, endIndex);

      return this.createSuccessResponse({
        segments: paginatedSegments,
        pagination: {
          page,
          limit,
          total: filteredSegments.length,
          totalPages: Math.ceil(filteredSegments.length / limit),
          hasNext: endIndex < filteredSegments.length,
          hasPrev: page > 1
        }
      }, request.requestId, 20);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  // Segment Membership Management
  async getSegmentMembers(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId, page = 1, limit = 50 } = request.parameters;
      
      const segment = this.segments.find(s => s.id === segmentId);
      if (!segment) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      const members = this.segmentMembers.filter(m => m.segmentId === segmentId && m.isActive);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedMembers = members.slice(startIndex, endIndex);

      // Get lead details for members
      const memberDetails = paginatedMembers.map(member => {
        const lead = this.leads.find(l => l.id === member.leadId);
        return {
          ...member,
          lead: lead || { id: member.leadId, name: 'Unknown Lead' }
        };
      });

      return this.createSuccessResponse({
        members: memberDetails,
        pagination: {
          page,
          limit,
          total: members.length,
          totalPages: Math.ceil(members.length / limit),
          hasNext: endIndex < members.length,
          hasPrev: page > 1
        }
      }, request.requestId, 25);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  async addSegmentMembers(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId } = request.parameters;
      const data = request.method === 'POST' ? request.body : request.parameters;
      const { leadIds } = data;

      if (!Array.isArray(leadIds) || leadIds.length === 0) {
        return this.createErrorResponse({
          code: 'VALIDATION_ERROR',
          message: 'leadIds must be a non-empty array'
        }, request.requestId, 0, 400);
      }

      const segment = this.segments.find(s => s.id === segmentId);
      if (!segment) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      const addedMembers: SegmentMember[] = [];
      const existingMembers = this.segmentMembers.filter(m => m.segmentId === segmentId);

      for (const leadId of leadIds) {
        // Check if member already exists
        const existingMember = existingMembers.find(m => m.leadId === leadId);
        
        if (!existingMember) {
          const newMember: SegmentMember = {
            segmentId,
            leadId,
            addedAt: new Date(),
            addedBy: request.agentId,
            isActive: true
          };
          
          this.segmentMembers.push(newMember);
          addedMembers.push(newMember);
        } else if (!existingMember.isActive) {
          // Reactivate existing inactive member
          existingMember.isActive = true;
          existingMember.addedAt = new Date();
          existingMember.addedBy = request.agentId;
          addedMembers.push(existingMember);
        }
      }

      // Update segment size
      const activeMembers = this.segmentMembers.filter(m => m.segmentId === segmentId && m.isActive);
      const segmentIndex = this.segments.findIndex(s => s.id === segmentId);
      this.segments[segmentIndex].size = activeMembers.length;
      this.segments[segmentIndex].updatedAt = new Date();

      return this.createSuccessResponse({
        addedCount: addedMembers.length,
        totalMembers: activeMembers.length,
        addedMembers
      }, request.requestId, 40);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  async removeSegmentMembers(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId } = request.parameters;
      const data = request.method === 'DELETE' ? request.body : request.parameters;
      const { leadIds } = data;

      if (!Array.isArray(leadIds) || leadIds.length === 0) {
        return this.createErrorResponse({
          code: 'VALIDATION_ERROR',
          message: 'leadIds must be a non-empty array'
        }, request.requestId, 0, 400);
      }

      const segment = this.segments.find(s => s.id === segmentId);
      if (!segment) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      let removedCount = 0;
      for (const leadId of leadIds) {
        const memberIndex = this.segmentMembers.findIndex(m => 
          m.segmentId === segmentId && m.leadId === leadId && m.isActive
        );
        
        if (memberIndex !== -1) {
          this.segmentMembers[memberIndex].isActive = false;
          removedCount++;
        }
      }

      // Update segment size
      const activeMembers = this.segmentMembers.filter(m => m.segmentId === segmentId && m.isActive);
      const segmentIndex = this.segments.findIndex(s => s.id === segmentId);
      this.segments[segmentIndex].size = activeMembers.length;
      this.segments[segmentIndex].updatedAt = new Date();

      return this.createSuccessResponse({
        removedCount,
        totalMembers: activeMembers.length
      }, request.requestId, 35);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  // Performance Tracking
  async getSegmentPerformance(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId } = request.parameters;
      
      const segment = this.segments.find(s => s.id === segmentId);
      if (!segment) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      // Mock performance data based on segment characteristics
      const performance: SegmentPerformance = {
        segmentId,
        totalCampaigns: segment.usageCount,
        totalEmails: segment.usageCount * 150, // Estimated emails per campaign
        averageOpenRate: segment.expectedPerformance.openRate + (Math.random() * 0.1 - 0.05), // Add some variance
        averageClickRate: segment.expectedPerformance.clickRate + (Math.random() * 0.02 - 0.01),
        averageConversionRate: segment.expectedPerformance.conversionRate + (Math.random() * 0.01 - 0.005),
        lastCampaignDate: segment.lastUsed,
        performanceTrend: segment.usageCount > 5 ? 'up' : segment.usageCount > 2 ? 'stable' : 'down',
        benchmarkComparison: {
          vsAverage: (segment.expectedPerformance.conversionRate - 0.025) * 100, // vs 2.5% platform average
          ranking: Math.floor(Math.random() * 10) + 1 // Random ranking for demo
        }
      };

      return this.createSuccessResponse(performance, request.requestId, 30);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  // Optimization Suggestions
  async getSegmentOptimizations(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    try {
      const { segmentId } = request.parameters;
      
      const segment = this.segments.find(s => s.id === segmentId);
      if (!segment) {
        return this.createErrorResponse({
          code: 'SEGMENT_NOT_FOUND',
          message: `Segment with ID ${segmentId} not found`
        }, request.requestId, 10, 404);
      }

      const optimizations: SegmentOptimization = {
        segmentId,
        suggestions: this.generateOptimizationSuggestions(segment),
        sizeRecommendation: this.generateSizeRecommendation(segment),
        criteriaRecommendations: this.generateCriteriaRecommendations(segment)
      };

      return this.createSuccessResponse(optimizations, request.requestId, 45);
    } catch (error) {
      return this.handleError(error, request.requestId);
    }
  }

  // Helper Methods
  private createSuccessResponse(data: any, requestId: string, processingTime: number, status: number = 200): AgentAPIResponse {
    return {
      status,
      headers: {},
      data,
      duration: processingTime,
      requestId,
      success: true,
      metadata: {
        requestId,
        timestamp: new Date(),
        processingTime,
        rateLimit: { remaining: 99, resetTime: new Date(Date.now() + 3600000) }
      }
    };
  }

  private createErrorResponse(error: any, requestId: string, processingTime: number = 0, status: number = 400): AgentAPIResponse {
    return {
      status,
      headers: {},
      data: null,
      duration: processingTime,
      requestId,
      success: false,
      error,
      metadata: {
        requestId,
        timestamp: new Date(),
        processingTime,
        rateLimit: { remaining: 100, resetTime: new Date(Date.now() + 3600000) }
      }
    };
  }

  private validateCriteria(criteria: any[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!Array.isArray(criteria)) {
      errors.push('Criteria must be an array');
      return { isValid: false, errors };
    }

    criteria.forEach((criterion, index) => {
      if (!criterion.field) {
        errors.push(`Criterion ${index + 1}: field is required`);
      }

      if (!criterion.operator) {
        errors.push(`Criterion ${index + 1}: operator is required`);
      }

      if (criterion.value === undefined || criterion.value === null) {
        errors.push(`Criterion ${index + 1}: value is required`);
      }

      if (index > 0 && !criterion.logicalOperator) {
        errors.push(`Criterion ${index + 1}: logicalOperator is required for chained criteria`);
      }
    });

    return { isValid: errors.length === 0, errors };
  }

  private calculateSegmentSize(criteria: SegmentCriteria[]): number {
    // Simplified calculation based on mock data
    let matchingLeads = this.leads.filter(lead => {
      return criteria.every(criterion => {
        const leadValue = lead[criterion.field];
        
        switch (criterion.operator) {
          case 'equals':
            return leadValue === criterion.value;
          case 'contains':
            return leadValue && leadValue.toString().toLowerCase().includes(criterion.value.toLowerCase());
          case 'greater_than':
            return leadValue > criterion.value;
          case 'less_than':
            return leadValue < criterion.value;
          case 'in':
            return Array.isArray(criterion.value) && criterion.value.includes(leadValue);
          case 'not_in':
            return Array.isArray(criterion.value) && !criterion.value.includes(leadValue);
          default:
            return false;
        }
      });
    });

    // Scale up for realistic numbers (mock data has only 5 leads)
    return Math.max(matchingLeads.length * 50, 1);
  }

  private generateOptimizationSuggestions(segment: AudienceSegment) {
    const suggestions = [];

    // Size-based suggestions
    if (segment.size < 100) {
      suggestions.push({
        type: 'size' as const,
        priority: 'high' as const,
        title: 'Segment Too Small',
        description: 'This segment may be too small for effective campaigns',
        expectedImpact: 'Increase reach by 300-500%',
        actionRequired: 'Consider broadening criteria or combining with similar segments'
      });
    } else if (segment.size > 5000) {
      suggestions.push({
        type: 'size' as const,
        priority: 'medium' as const,
        title: 'Consider Segmentation',
        description: 'Large segments may benefit from further subdivision',
        expectedImpact: 'Improve targeting precision by 20-30%',
        actionRequired: 'Add additional criteria to create more focused sub-segments'
      });
    }

    // Performance-based suggestions
    if (segment.expectedPerformance.conversionRate < 0.02) {
      suggestions.push({
        type: 'performance' as const,
        priority: 'high' as const,
        title: 'Low Conversion Rate',
        description: 'This segment shows below-average conversion potential',
        expectedImpact: 'Potential 50-100% improvement in conversions',
        actionRequired: 'Review criteria to focus on higher-quality leads'
      });
    }

    // Engagement-based suggestions
    if (segment.usageCount === 0) {
      suggestions.push({
        type: 'engagement' as const,
        priority: 'medium' as const,
        title: 'Unused Segment',
        description: 'This segment has not been used in any campaigns',
        expectedImpact: 'Activate dormant audience potential',
        actionRequired: 'Create a campaign targeting this segment or archive if no longer needed'
      });
    }

    return suggestions;
  }

  private generateSizeRecommendation(segment: AudienceSegment) {
    let recommended = segment.size;
    let reason = 'Current size is optimal for most campaign types';

    if (segment.size < 100) {
      recommended = 250;
      reason = 'Minimum recommended size for statistical significance in A/B testing';
    } else if (segment.size > 10000) {
      recommended = 5000;
      reason = 'Smaller segments often perform better due to increased relevance';
    }

    return {
      current: segment.size,
      recommended,
      reason
    };
  }

  private generateCriteriaRecommendations(segment: AudienceSegment) {
    const recommendations: any = {};

    // Suggest additional criteria based on existing ones
    if (segment.criteria.some(c => c.field === 'status' && c.value === 'hot')) {
      recommendations.add = [{
        id: 'suggested_engagement',
        field: 'lastEngaged',
        operator: 'greater_than',
        value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
        weight: 0.8,
        logicalOperator: 'AND'
      }];
    }

    // Suggest modifications to existing criteria
    const lowScoreCriteria = segment.criteria.find(c => c.field === 'leadScore' && c.value < 50);
    if (lowScoreCriteria) {
      recommendations.modify = [{
        criteriaId: lowScoreCriteria.id,
        suggestion: 'Consider increasing the minimum lead score to 60 for better conversion rates'
      }];
    }

    return recommendations;
  }

  private handleError(error: any, requestId: string): AgentAPIResponse {
    console.error('AgentSegmentService error:', error);
    
    return {
      status: 500,
      headers: {},
      data: null,
      duration: 0,
      requestId,
      success: false,
      error: {
        code: 'INTERNAL_ERROR',
        message: 'An internal error occurred while processing the request',
        details: process.env.NODE_ENV === 'development' ? error.message : undefined
      },
      metadata: {
        requestId,
        timestamp: new Date(),
        processingTime: 0,
        rateLimit: { remaining: 100, resetTime: new Date(Date.now() + 3600000) }
      }
    };
  }
}